export type CachedModel = {
  id: string
  fileUrl: string
  hash?: string
  size?: number
  stats?: ModelCacheStats
  sourceUrl?: string
  expiresAt?: string
  lastAccessedAt?: string
  accessCount?: number
}

export type ModelCacheStats = {
  itemCount: number
  totalSize: number
}

const MODEL_CACHE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getApiEndpoint = () => {
  const baseUrl = import.meta.env.BASE_URL || '/'
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`

  return new URL(`${normalizedBaseUrl}api/model-cache`, window.location.origin)
}

const getResponseError = (data: unknown, fallback: string) => {
  if (isObject(data) && typeof data.error === 'string' && data.error.trim()) {
    return data.error
  }

  return fallback
}

const parseCacheStatsData = (data: unknown): ModelCacheStats => {
  if (
    !isObject(data)
    || typeof data.itemCount !== 'number'
    || typeof data.totalSize !== 'number'
    || !Number.isFinite(data.itemCount)
    || !Number.isFinite(data.totalSize)
    || data.itemCount < 0
    || data.totalSize < 0
  ) {
    throw new Error('The model cache returned incomplete stats data.')
  }

  return {
    itemCount: Math.trunc(data.itemCount),
    totalSize: Math.trunc(data.totalSize)
  }
}

const parseOptionalCacheStats = (data: unknown) => {
  if (!isObject(data) || data.stats === undefined) {
    return undefined
  }

  try {
    return parseCacheStatsData(data.stats)
  } catch {
    return undefined
  }
}

const parseCacheResponse = async (response: Response): Promise<CachedModel> => {
  let data: unknown

  try {
    data = await response.json()
  } catch {
    throw new Error('The model cache returned an invalid response.')
  }

  if (!response.ok) {
    throw new Error(getResponseError(data, `Model cache request failed with status ${response.status}.`))
  }

  if (
    !isObject(data)
    || typeof data.id !== 'string'
    || typeof data.fileUrl !== 'string'
    || !isModelCacheId(data.id)
  ) {
    throw new Error('The model cache returned incomplete model data.')
  }

  return {
    id:             data.id.toLowerCase(),
    fileUrl:        new URL(data.fileUrl, window.location.origin).toString(),
    hash:           typeof data.hash === 'string' ? data.hash : undefined,
    size:           typeof data.size === 'number' ? data.size : undefined,
    stats:          parseOptionalCacheStats(data),
    sourceUrl:      typeof data.sourceUrl === 'string' ? data.sourceUrl : undefined,
    expiresAt:      typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
    lastAccessedAt: typeof data.lastAccessedAt === 'string' ? data.lastAccessedAt : undefined,
    accessCount:    typeof data.accessCount === 'number' ? data.accessCount : undefined
  }
}

const parseCacheStatsResponse = async (response: Response): Promise<ModelCacheStats> => {
  let data: unknown

  try {
    data = await response.json()
  } catch {
    throw new Error('The model cache returned an invalid stats response.')
  }

  if (!response.ok) {
    throw new Error(getResponseError(data, `Model cache stats request failed with status ${response.status}.`))
  }

  return parseCacheStatsData(data)
}

export const isModelCacheId = (value: string) => MODEL_CACHE_ID_PATTERN.test(value.trim())

export const cacheRemoteModelUrl = async (modelUrl: string, signal?: AbortSignal) => {
  const response = await fetch(getApiEndpoint(), {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ url: modelUrl }),
    signal
  })

  return parseCacheResponse(response)
}

export const resolveCachedModel = async (id: string, signal?: AbortSignal) => {
  const endpoint = getApiEndpoint()

  endpoint.searchParams.set('id', id.trim())

  return parseCacheResponse(await fetch(endpoint, { signal }))
}

export const getModelCacheStats = async (signal?: AbortSignal) => {
  const endpoint = getApiEndpoint()

  endpoint.searchParams.set('stats', '1')

  return parseCacheStatsResponse(await fetch(endpoint, { signal }))
}
